import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Calendar, Clock, User } from "lucide-react";
import { BLOG_POSTS } from "../lib/data";
import DemoRequest from "../components/sections/DemoRequest";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <article data-testid={`blog-post-${slug}`}>
      <section className="relative pt-16 pb-20 sm:pt-24 sm:pb-28 border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-oj-ink">
            <ArrowLeft className="size-4" /> All posts
          </Link>
          <div className="mt-8 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: post.color }} />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">{post.category}</span>
          </div>
          <h1 className="font-display text-balance mt-4 text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-oj-ink max-w-4xl">
            {post.title}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-zinc-600">
            <span className="inline-flex items-center gap-1.5"><User className="size-4" /> {post.author}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="size-4" /> {new Date(post.date).toLocaleDateString()}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="size-4" /> {post.read_minutes} min read</span>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="container-oj grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-6">
            <p className="text-xl leading-relaxed text-oj-ink pretty">{post.excerpt}</p>
            {post.body.map((b, i) => {
              if (b.type === "h2")
                return (
                  <h2 key={i} className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-oj-ink mt-10">
                    {b.text}
                  </h2>
                );
              return (
                <p key={i} className="text-lg leading-relaxed text-zinc-700 pretty">
                  {b.text}
                </p>
              );
            })}
          </div>
          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl border border-zinc-200 p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Keep reading</div>
              <ul className="mt-4 space-y-4">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link to={`/blog/${o.slug}`} className="group block">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{o.category}</div>
                      <div className="mt-1 font-display font-bold text-oj-ink leading-tight group-hover:underline">{o.title}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/#demo"
              className="block rounded-2xl bg-oj-ink text-white p-6 hover:bg-zinc-800 transition-colors"
              data-testid="blog-cta-demo"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">Get started</div>
              <div className="mt-2 font-display text-2xl font-black tracking-tight">Run this on your menu in 30 minutes.</div>
              <div className="mt-4 inline-flex items-center gap-1 text-sm">Book a demo <ArrowUpRight className="size-4" /></div>
            </Link>
          </aside>
        </div>
      </section>

      <DemoRequest />
    </article>
  );
}
